import Select from "react-select";
import styled from "styled-components";
import { useHistory } from "react-router";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { RootState } from "../../store/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { PermissionMenuName } from "../../interfaces/UserRole";
import { getRoleAsync } from "../../reducers/userRole/userRole.actions";
import { getEmployeeAsync } from "../../reducers/employee/employee.actions";
import { fieldErrorMessage, fieldHasErrors } from "../../store/errorHandler";
import { allowDelete, allowSave, getSelectedStatusOption, STATUS_OPTIONS } from "../../store/helpers";
import { createUserAsync, getDetailUserAsync, initializeCreateUser, updateEmployee, updateRole, updateStatus, updateUserAsync } from "../../reducers/user/user.actions";

interface Option { value: string; label: string; }

export const UserUpdatePage = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const [isNew, setIsNew] = useState<boolean>(false);
  const errors = useSelector((state: RootState) => state.user.error);
  const user = useSelector((state: RootState) => state.user.selectedUser);
  const listRole = useSelector((state: RootState) => state.userRole.listRoles);
  const isLoadingUser = useSelector((state: RootState) => state.user.isLoading);
  const isLoadingRole = useSelector((state: RootState) => state.userRole.isLoading);
  const redirectQueue = useSelector((state: RootState) => state.user.redirectQueue);
  const listEmployee = useSelector((state: RootState) => state.employee.listEmployee);
  const isLoadingEmployee = useSelector((state: RootState) => state.employee.isLoading);
  const [roleOptions, setRoleOptions] = useState<Option[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<Option[]>([]);
  const isLoading = isLoadingUser && isLoadingRole && isLoadingEmployee;
  const role = useSelector((state: RootState) => state.authorization.systemAccess[PermissionMenuName.userControl].ACCESS);

  const canSave = allowSave(role, 2);

  useEffect(() => {
    dispatch(getEmployeeAsync.request());
    dispatch(getRoleAsync.request());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const newOptions: Option[] = [];
    listEmployee.forEach(item => {
      newOptions.push({
        value: item.EMP_ID.toString(),
        label: item.EMP_NAME
      })
    });
    setEmployeeOptions(newOptions);
  }, [listEmployee]);

  useEffect(() => {
    const newOptions: Option[] = [];
    listRole.forEach(item => {
      if (item.NAME !== 'Root Admin') {
        newOptions.push({
          value: item.ROLE_ID.toString(),
          label: item.NAME
        })
      }
    });
    setRoleOptions(newOptions);
  }, [listRole]);
  
  useEffect(() => {
    const paths = location.pathname.split("/");
    if (paths[2] === 'create') {
      setIsNew(true);
      dispatch(initializeCreateUser());
    } else {
      setIsNew(false);
      const id = parseInt(paths[2]);
      if (id && !isNaN(id)) {
        dispatch(getDetailUserAsync.request({ id }));
      } else {
        history.push(`/user-control/create`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (redirectQueue) {
      history.push(redirectQueue);
      dispatch(initializeCreateUser());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectQueue])

  const getSelectedEmployeeOption = () => {
    if (!user) return null;
    if (!user.NIK_DETAIL) return null;
    const selection = user?.NIK_DETAIL;
    return { value: selection.EMP_ID.toString(), label: selection.EMP_NAME };
  }

  const onChangeEmployee = (e: Option | null) => {
    if (!e) return;
    const id = e.value;
    const newPicUMN = listEmployee.find(search => search.EMP_ID === id);
    if (newPicUMN) {
      dispatch(updateEmployee(e.value, newPicUMN));
    }
  }

  const getSelectedRoleOption = () => {
    if (!user) return null;
    if (!user.ROLE_DETAIL) return null;
    const selection = user?.ROLE_DETAIL;
    return { value: selection.ROLE_ID.toString(), label: selection.NAME };
  }

  const onChangeRole = (e: Option | null) => {
    if (!e) return;

    const id = parseInt(e.value);
    if (isNaN(id)) return;

    const newRole = listRole.find(search => search.ROLE_ID === id);

    if (newRole) {
      dispatch(updateRole(id, newRole));
    }
  }

  const onChangeStatus = (e: Option | null) => {
    if (!e) return;
    if (isNaN(parseInt(e.value))) return;
    dispatch(updateStatus(parseInt(e.value)));
  }

  const save = () => {
    if (!user) return;
    if (isNew) {
      dispatch(createUserAsync.request(user))
    } else {
      dispatch(updateUserAsync.request(user))
    }
  }
 
  return (
    <UserUpdatePageWrapper>
      <section className="content">
        <div className="row">
          <div className="col-lg-5">
            <div className="box">
              <div className="box-header with-border">
                <h3 className="box-title">Detail Pengguna</h3>
                <div className="box-tools">
                  <button
                    className="btn btn-primary"
                    style={{marginRight: '4px'}}
                    onClick={() => history.push('/user-control')}
                  >
                    kembali
                  </button>
                  <button
                    onClick={save}
                    className="btn btn-primary"
                    disabled={isLoading || !canSave}
                  >
                    Simpan
                  </button>
                </div>
              </div>
              <div className="box-body">
                <div className={`form-group ${fieldHasErrors('EMP_ID', errors) ? 'has-error' : ''}`}>
                  <label style={{marginBottom: 0}}>Nama Karyawan Tetap <span className="text-red"><b>*</b></span></label> <br />
                  <label style={{fontSize: '10pt'}} className="text-red"><i>Hanya bisa menginput karyawan tetap. Jika karyawan tidak ditemukan, silakan menghubungi HR</i></label>
                  <Select 
                    isDisabled={isLoading || !canSave}
                    options={employeeOptions}
                    onChange={onChangeEmployee}
                    value={getSelectedEmployeeOption()}
                  />
                  <span className="help-block">{ fieldErrorMessage('EMP_ID', errors) }</span>
                </div>
                <div className={`form-group ${fieldHasErrors('ROLE_ID', errors) ? 'has-error' : ''}`}>
                  <label>Role <span className="text-red"><b>*</b></span></label>
                  <Select 
                    isDisabled={isLoading || !canSave}
                    options={roleOptions}
                    onChange={onChangeRole}
                    value={getSelectedRoleOption()}
                  />
                  <span className="help-block">{ fieldErrorMessage('ROLE_ID', errors) }</span>
                </div>
                <div className="form-group">
                  <label>Status <span className="text-red"><b>*</b></span></label>
                  <Select 
                    isDisabled={isLoading || !allowDelete(role, 2)}
                    options={STATUS_OPTIONS}
                    onChange={onChangeStatus}
                    value={getSelectedStatusOption(user?.STATUS)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </UserUpdatePageWrapper>
  );
}

const UserUpdatePageWrapper = styled.div``;